import { FileManagementRequest, FileManagement, FileSignedDownloadUrl } from 'api/model';
import instance, { BASE_URL, multipartInstance } from '../axios_instance';
import { INNER_FILE_DOWNLOAD_BASE_URL } from 'api/config';

export type Progress = {
  loaded: number;
  total: number;
  progress: number;
};

export type ProgressCallback = (progressEvent: Progress) => void;

export const getProjectList = async (projectId: string, includeGlobalFiles: boolean): Promise<FileManagement[]> => {
  let url = `/projects/${projectId}/file-management/files`;
  if (includeGlobalFiles) {
    url = `${url}?include_global=true`;
  }
  const response = await instance.get<FileManagement[]>(url);
  return response.data;
};

export const getGlobalList = async (): Promise<FileManagement[]> => {
  const response = await instance.get<FileManagement[]>('/file-management/files');
  return response.data;
};

export const getInfo = async (id: string): Promise<FileManagement> => {
  const response = await instance.get<FileManagement>(`/file-management/files/${id}/info`);
  return response.data;
};

function isFile(obj: unknown): obj is File {
  return typeof File !== 'undefined' && obj instanceof File;
}

export const uploadProject = async (
  projectId: string,
  model: FileManagementRequest,
  onProgress?: ProgressCallback,
): Promise<FileManagement> => {
  const form = new FormData();
  if (!isFile(model.file)) {
    throw new Error('FileManagementRequest.file must be a File object');
  }
  form.append('file', model.file);
  const response = await multipartInstance.post<FileManagement>(`/projects/${projectId}/file-management/files`, form, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          progress,
        });
      }
    },
  });
  return response.data;
};

export const uploadGlobal = async (
  model: FileManagementRequest,
  onProgress?: ProgressCallback,
): Promise<FileManagement> => {
  const form = new FormData();
  if (!isFile(model.file)) {
    throw new Error('FileManagementRequest.file must be a File object');
  }
  form.append('file', model.file);
  const response = await multipartInstance.post<FileManagement>('/file-management/files', form, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          progress,
        });
      }
    },
  });
  return response.data;
};

export const getSignedDownloadUrl = async (fileId: string): Promise<FileSignedDownloadUrl> => {
  const response = await instance.get<FileSignedDownloadUrl>(
    `${BASE_URL}/file-management/files/${fileId}/signed-download-url`,
  );
  return response.data;
};

export const innerDownloadLink = (fileId: string): string => {
  return `${INNER_FILE_DOWNLOAD_BASE_URL}/file-management/files/${fileId}`;
};

export const remove = async (fileId: string) => {
  return await instance.delete(`/file-management/files/${fileId}`);
};
